type ScoreBarProps = {
  label: string;
  /** Null renders as null. Composition §6: a zero bar is a lie about how the score was computed. */
  value: number | null;
  /** Scores arrive 0–1 from criteria, 0–4 from X review dimensions, 0–100 from X review totals. */
  scale?: 1 | 4 | 100;
  nullNote?: string;
  /** Trailing context on the same line — a weight, a contribution, a threshold comparison. */
  note?: string;
};

/** A dimension graded 3 of 4 must read "3 / 4", not "3" on a track that looks empty. */
function formatValue(value: number, scale: 1 | 4 | 100): string {
  if (scale === 1) return value.toFixed(2);
  if (scale === 4) return `${value} / 4`;
  return String(Math.round(value));
}

/**
 * A score is never shown without a way to read it. The bar carries no hue of its own — Art
 * Direction §8: a number is only tinted when it crosses a documented threshold, and none of these
 * do.
 */
export function ScoreBar({ label, value, scale = 1, nullNote = 'no se ejecutó', note }: ScoreBarProps) {
  if (value === null) {
    return (
      <div className="score" data-null="true">
        <span className="score__label">{label}</span>
        <span className="score__null">— {nullNote}</span>
      </div>
    );
  }

  const fraction = Math.max(0, Math.min(value / scale, 1));
  return (
    <div className="score">
      <span className="score__label">{label}</span>
      <span className="score__value">{formatValue(value, scale)}</span>
      <span
        className="score__track"
        role="meter"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={scale}
      >
        <span className="score__fill" style={{ inlineSize: `${fraction * 100}%` }} />
      </span>
      {note && <span className="score__note">{note}</span>}
    </div>
  );
}
